import React from "react";
import * as R from "ramda";
import { Avatar, Icon, List, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import { connect } from "react-redux";

import Spinner from "../../../../components/feedback/Spinner";
import { getOptimizedAvatar } from "../../../../utils/images";
import { vinculationStatuses } from "../../../List/VinculationsTable";
import RejectForm from "../../../List/RejectForm";
import {
  linkToCompanies,
  LINK_TO_COMPANIES
} from "../../../../store/actions/company";
import { isLoading } from "../../../../utils/actions";
import { getCompanyURL } from "../../../../utils/urls";
import AcceptLinkSuccess from "../../../List/AcceptLinkSuccess";

import styled, { css } from "styled-components/macro";

const Title = styled.div`
  display: flex;
  justify-content: space-between;

  .title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ItemLink = styled(Link)`
  width: 100%;
`;

const NotificationContainer = styled.div`
  width: 100%;
  padding: 20px 10px;
  position: relative;
  ${({ withAvatar }) =>
    withAvatar &&
    css`
      .ant-avatar {
        border: solid 1px #e5e9ec;
        background-color: #ffffff;
      }
    `}

  .ant-list-item-meta-description {
    text-overflow: inherit;
    overflow: visible;
    white-space: inherit;
  }
`;

const StatusText = styled.span`
  font-size: 14px;
  line-height: 19px;
  color: #747c84;
  display: inline-flex;
  flex: 1;
  padding-right: 20px;
  transition: 0.3s ease;
  white-space: normal;
  ${props => props.type === "link" && `cursor: pointer;`}
  &:hover {
    color: ${props => props.type === "link" && props.theme["@primary"]};
  }
`;
const CompanyName = styled.span`
  color: #343a40;
  cursor: pointer;
  transition: 0.3s ease;
  &:hover {
    color: ${({ theme }) => theme["@primary"]};
  }
`;

const Time = styled.span`
  color: #747c84;
  font-size: 10px;
  margin-right: 10px;
`;

const StatusActionContainer = styled.div`
  display: inline-flex;
  flex: 0 1 80px;
  justify-content: space-between;
  font-size: 28px;
  position: relative;
`;

const StatusLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
`;

const StatusOptions = styled.div`
  display: flex;
  transition: 0.3s ease;
  width: 100%;
  ${props =>
    props.isLoading &&
    `
    filter: blur(3px);
    opacity: 0.5;
  `}
`;

const StatusAction = styled.button`
  display: flex;
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 5px;
  transition: 0.3s ease;
  transform: scale(0.8);
  &:hover {
    transform: scale(1);
  }
`;

const PendingItem = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Goto = styled.div`
  flex: 0 0 28px;
  padding: 0;
  font-size: 28px;
  justify-content: flex-end;
  transition: 0.3s ease;
  transform: scale(0.8);
  &:hover {
    transform: scale(1);
  }
`;

const Article = styled.article`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 8px;
`;

const ArticleHeader = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: #343a40;
  text-align: center;
  margin-top: 18px;
  width: 100%;
`;

const ArticleDescription = styled.p`
  font-size: 14px;
  color: #343a40;
  margin-top: 18px;
`;

const RedirectLink = styled.a`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  &:after {
    content: "";
    display: flex;
    position: absolute;
    left: -100%;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: #3d64ff;
    border-radius: 4px;
    transition: 0.3s ease;
  }
`;

const ArticleLink = styled.a`
  color: #3d64ff;
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;
  .anticon {
    margin-right: 10px;
    transition: 0.3s ease;
  }

  &:hover {
    .anticon {
      transform: translateX(5px);
    }
    ${RedirectLink} {
      &:after {
        transition: 0.3s 0.3s ease;
        transform: translateX(100%);
      }
    }
  }
`;

const ArticleActions = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-top: 32px;
`;

const ModalAction = styled.button`
  cursor: pointer;
  font-size: 16px;
  height: 40px;
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: ${props => (props.type === "accept" ? "280px" : "114px")};
  background: ${props => (props.type === "accept" ? "#3D64FF" : "none")};
  border: 1px solid black;
  border-radius: 4px;
  border-color: ${props =>
    props.type === "accept" ? "transparent" : "#ff4d4e"};
  color: ${props => (props.type === "accept" ? "white" : "#ff4d4e")};
  transition: 0.3s ease;
  &:hover {
    opacity: 0.6;
  }
`;

const Notification = ({
  notification,
  setShowPopover,
  isLoading,
  linkToCompanies,
  company
}) => {
  const { t } = useTranslation(["notifications", "components"]);
  const [status, setStatus] = React.useState("pending");
  const [modal, setModal] = React.useState(false);
  const [rejectModal, setRejectModal] = React.useState(false);

  const handleReject = async reason => {
    try {
      setRejectModal(false);
      await linkToCompanies([
        {
          idCompany: notification.companyId,
          status: vinculationStatuses.rejected,
          reason
        }
      ]);
      setStatus("rejected");
    } catch (e) {
      console.error(e);
    }
  };

  const handleAccept = async () => {
    try {
      await linkToCompanies([
        {
          idCompany: notification.companyId,
          status: vinculationStatuses.linked
        }
      ]);
      setShowPopover(false);
      const modal = Modal.info({
        icon: null,
        okButtonProps: { style: { display: "none" } },
        content: (
          <AcceptLinkSuccess
            company={company}
            vinculations={[{ name: notification.companyName }]}
            onContinue={() => modal.destroy()}
            t={t}
          />
        ),
        maskClosable: true
      });
      setStatus("accepted");
    } catch (e) {
      console.error(e);
    }
  };

  const showModal = () => {
    setShowPopover(false);
    setModal(true);
  };

  return (
    <List.Item data-test="new-notification">
      <NotificationContainer
        data-test="vinculation-notification"
        withAvatar={!!notification.companyLogo}
      >
        <List.Item.Meta
          avatar={
            <Avatar
              size={50}
              onClick={showModal}
              src={getOptimizedAvatar(notification.companyLogo)}
              icon="link"
            />
          }
          title={
            <Title>
              <CompanyName
                onClick={showModal}
                data-test="new-notification-title"
                className="title"
              >
                {notification.companyName}
              </CompanyName>
              <Time className="timestamp">
                {moment(notification.timestamp).isSame(Date.now(), "day")
                  ? moment(notification.timestamp).format("HH:mm")
                  : moment(notification.timestamp).isSame(Date.now(), "year")
                  ? moment(notification.timestamp).format("MMM D")
                  : moment(notification.timestamp).format("L")}
              </Time>
            </Title>
          }
          description={
            status === "pending" ? (
              <PendingItem>
                <StatusText
                  type="link"
                  onClick={showModal}
                  data-test="new-notification-text"
                >
                  {t("notifications:status.pending")}
                </StatusText>
                {notification.opportunityId && (
                  <ItemLink
                    onClick={() => setShowPopover(false)}
                    to={`/compañia/vinculations`}
                  >
                    <span>Ver oportunidad</span>
                  </ItemLink>
                )}
                <StatusActionContainer>
                  {isLoading && (
                    <StatusLoading>
                      <Spinner />
                    </StatusLoading>
                  )}

                  <StatusOptions isLoading={isLoading}>
                    <StatusAction
                      data-test="reject-vinculation-notification"
                      onClick={() => {
                        setRejectModal(true);
                        setShowPopover(false);
                      }}
                    >
                      <Icon
                        theme="twoTone"
                        twoToneColor={"#ff4d4e"}
                        type="close-circle"
                      />
                    </StatusAction>
                    <StatusAction
                      onClick={handleAccept}
                      data-test="accept-vinculation-notification"
                    >
                      <Icon
                        theme="twoTone"
                        twoToneColor={"#3d64ff"}
                        type="check-circle"
                      />
                    </StatusAction>
                  </StatusOptions>
                </StatusActionContainer>
              </PendingItem>
            ) : status === "rejected" ? (
              <StatusText data-test="new-notification-text">
                {t("notifications:status.rejected")}
              </StatusText>
            ) : status === "accepted" ? (
              <>
                <StatusText>{t("notifications:status.accepted")}</StatusText>
                <Goto>
                  <ItemLink
                    onClick={() => setShowPopover(false)}
                    to={`/compañia/vinculations`}
                  >
                    <Icon type="right-circle" />
                  </ItemLink>
                </Goto>
              </>
            ) : (
              <StatusText>{t("notifications:status.error")}</StatusText>
            )
          }
        />
      </NotificationContainer>

      <Modal
        width={480}
        onCancel={() => setRejectModal(false)}
        visible={rejectModal}
        footer={null}
      >
        <RejectForm
          onCancel={() => setRejectModal(false)}
          onSubmit={handleReject}
          companies={[{ name: notification.companyName }]}
          t={t}
        />
      </Modal>

      <Modal
        width={480}
        onCancel={() => setModal(false)}
        visible={modal}
        footer={null}
      >
        <Article data-test="vinculation-notification-modal">
          <ArticleHeader>
            {`La empresa ${notification.companyName} está solicitando permiso para poder hacer visibles sus vacantes laborales en vuestra bolsa de trabajo.`}
          </ArticleHeader>
          <ArticleDescription>
            Esto ayudará a incentivar e incrementar la salida laboral de sus
            estudiantes. <br />
            Si quieres, puedes <strong>Aprobar</strong> la vinculación ahora o
            dejarlo para más adelante, te lo recordaremos en otro momento.
          </ArticleDescription>
          <ArticleLink>
            <Icon type="arrow-right" />
            <RedirectLink
              href={getCompanyURL(notification.companyName)}
              target="_blank"
              rel="noreferrer noopener"
            >
              {`Visitar bolsa de empleo de ${notification.companyName}`}
            </RedirectLink>
          </ArticleLink>
          <ArticleActions>
            <ModalAction
              onClick={() => {
                setModal(false);
                setRejectModal(true);
              }}
              type="reject"
              data-test="reject-vinculation-modal"
            >
              Rechazar
            </ModalAction>
            <ModalAction
              onClick={() => {
                setModal(false);
                handleAccept();
              }}
              type="accept"
              data-test="accept-vinculation-modal"
            >
              Aprobar
            </ModalAction>
          </ArticleActions>
        </Article>
      </Modal>
    </List.Item>
  );
};

export default connect(
  state => ({
    isLoading: isLoading(LINK_TO_COMPANIES, state),
    company: R.propOr({}, "company", state)
  }),
  {
    linkToCompanies
  }
)(Notification);
